import React, { useState, useEffect } from 'react';
import { Target, CheckCircle, XCircle, RotateCcw, Brain, AlertTriangle, Clock, DollarSign } from 'lucide-react';
import MathExpression from './MathExpression';
import { submitDrillAnswer, getDrillResults, getEnhancedDrillResults, validatePrimaryDrillQuestion } from '../data/drillData';

const PrimaryDrillQuestions = ({ 
  drillData, 
  originalQuestionId, 
  onDrillComplete, 
  onGenerateNewDrill,
  progressData = null
}) => {
  const [selectedDrill, setSelectedDrill] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [currentInput, setCurrentInput] = useState({}); // Track current input before submission
  const [results, setResults] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (drillData && drillData.drillQuestions) {
      // Ensure all drill questions have required fields before validation
      const processedDrills = drillData.drillQuestions.map((drill, index) => {
        // Ensure id exists
        if (!drill.id) {
          drill.id = `drill_${originalQuestionId || 'unknown'}_${index + 1}`;
        }
        // Ensure questionNumber exists
        if (!drill.questionNumber) {
          drill.questionNumber = index + 1;
        }
        return drill;
      });
      
      // Validate drill questions
      const errors = [];
      processedDrills.forEach((drill, index) => {
        if (!validatePrimaryDrillQuestion(drill)) {
          errors.push(`Drill ${index + 1}: Invalid structure`);
        }
      });
      setValidationErrors(errors);

      // Load existing user answers
      const existingAnswers = {};
      processedDrills.forEach(drill => {
        const storedResults = getDrillResults(originalQuestionId);
        if (storedResults && storedResults.drillAnswers[drill.id]) {
          existingAnswers[drill.id] = storedResults.drillAnswers[drill.id];
        }
      });
      setUserAnswers(existingAnswers);
      
      // Reset current input when new drill data arrives
      setCurrentInput({});
      
      // Reset selected drill to allow user to select again
      setSelectedDrill(null);
      
      // Check if all drills are completed
      const drillResults = getEnhancedDrillResults(originalQuestionId, processedDrills);
      if (drillResults && drillResults.isComplete) {
        setResults(drillResults);
      } else {
        // Clear results if drills are not complete (new set generated)
        setResults(null);
      }
    }
  }, [drillData, originalQuestionId]);

  const handleDrillSelect = (drillQuestion) => {
    setSelectedDrill(drillQuestion);
    // Initialize current input if there's no submitted answer yet
    if (userAnswers[drillQuestion.id] === undefined && !currentInput[drillQuestion.id]) {
      setCurrentInput(prev => ({
        ...prev,
        [drillQuestion.id]: ''
      }));
    }
  };

  const handleAnswerSelect = (drillQuestionId, answer) => {
    const result = submitDrillAnswer(originalQuestionId, drillQuestionId, answer);
    
    if (result) {
      setUserAnswers(prev => ({
        ...prev,
        [drillQuestionId]: answer
      }));
      
      // Clear current input after submission
      setCurrentInput(prev => {
        const newInput = { ...prev };
        delete newInput[drillQuestionId];
        return newInput;
      });
      
      // Check if all drills are completed
      const updatedResults = getEnhancedDrillResults(originalQuestionId, drillData.drillQuestions);
      if (updatedResults && updatedResults.isComplete) {
        setResults(updatedResults);
        if (onDrillComplete) {
          onDrillComplete(updatedResults);
        }
      }
    }
  };

  const handleInputChange = (drillQuestionId, value) => {
    // Only update the current input state, don't submit yet
    setCurrentInput(prev => ({
      ...prev,
      [drillQuestionId]: value
    }));
  };

  const handleSubmitAnswer = (drillQuestionId) => {
    const answer = currentInput[drillQuestionId] || '';
    if (answer.trim() !== '') {
      handleAnswerSelect(drillQuestionId, answer.trim());
    }
  };

  const handleKeyPress = (e, drillQuestionId) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitAnswer(drillQuestionId);
    }
  };

  const handleGenerateNewDrill = () => {
    if (onGenerateNewDrill) {
      onGenerateNewDrill();
    }
  };

  if (!drillData || !drillData.drillQuestions) {
    return (
      <div className="text-center p-6">
        <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">載入小學練習題目中...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Drill Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Brain className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-green-800">AI 小學練習題</h3>
              <p className="text-sm text-green-600">
                基於原題生成的 {drillData.totalDrills} 道相似練習題
              </p>
              {drillData.metadata?.isRealAIGenerated && (
                <p className="text-xs text-green-600 mt-1">
                  🤖 由 DeepSeek AI 即時生成
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleGenerateNewDrill}
            className="flex items-center space-x-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            <span>生成新練習</span>
          </button>
        </div>
      </div>

      {/* Progress Information */}
      {progressData && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-blue-800 flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              AI 生成進度
            </h4>
            <div className="flex items-center space-x-4 text-sm">
              <span className="text-blue-600">
                時間: {progressData.timeElapsed}s
              </span>
              <span className="text-blue-600">
                令牌: {progressData.tokensUsed}/{progressData.estimatedTokens}
              </span>
              {progressData.costIncurred && (
                <span className="text-blue-600 flex items-center">
                  <DollarSign className="w-3 h-3 mr-1" />
                  成本: ¥{progressData.costIncurred.toFixed(4)}
                </span>
              )}
            </div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((progressData.tokensUsed / progressData.estimatedTokens) * 100, 100)}%` }}
            ></div>
          </div>
          <p className="text-sm text-blue-700 mt-2">{progressData.message}</p>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h4 className="font-semibold text-red-800">數據驗證警告</h4>
          </div>
          <ul className="text-sm text-red-700 space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Drill Progress */}
      {results && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800">練習進度</h4>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {results.answeredDrills} / {results.totalDrills} 題
              </span>
              <div className="w-24 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(results.answeredDrills / results.totalDrills) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
          
                     {results.isComplete && (
             <div className={`p-3 rounded-lg ${
               results.percentage >= 80 ? 'bg-green-50 border border-green-200' :
               results.percentage >= 60 ? 'bg-yellow-50 border border-yellow-200' :
               'bg-red-50 border border-red-200'
             }`}>
               <div className="flex items-center justify-between">
                 <span className="font-medium">
                   完成度: {results.percentage}% ({results.correctAnswers}/{results.totalDrills} 正確)
                 </span>
                 {results.percentage >= 80 && <CheckCircle className="w-5 h-5 text-green-600" />}
                 {results.percentage >= 60 && results.percentage < 80 && <Target className="w-5 h-5 text-yellow-600" />}
                 {results.percentage < 60 && <XCircle className="w-5 h-5 text-red-600" />}
               </div>
               <div className="mt-2 text-sm text-gray-600">
                 <span className="font-medium">評分標準：</span>
                 {results.percentage >= 80 ? '優秀！繼續保持！' :
                  results.percentage >= 60 ? '良好，還有進步空間' :
                  '需要更多練習，加油！'}
               </div>
             </div>
           )}
        </div>
      )}

      {/* Drill Selection Buttons */}
      <div className="grid grid-cols-5 gap-3">
        {(drillData?.drillQuestions || []).map((drill, index) => {
          // Ensure id exists for display
          const drillId = drill.id || `drill_${originalQuestionId || 'unknown'}_${index + 1}`;
          const userAnswer = userAnswers[drillId];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = isAnswered && userAnswer === drill.correctAnswer;
          
          return (
            <button
              key={drillId}
              onClick={() => handleDrillSelect(drill)}
              className={`relative w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-200 ${
                selectedDrill?.id === drillId
                  ? 'bg-green-500 text-white shadow-lg scale-110'
                  : isAnswered
                    ? isCorrect
                      ? 'bg-green-500 text-white'
                      : 'bg-red-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {index + 1}
              {isAnswered && (
                <div className="absolute -top-1 -right-1">
                  {isCorrect ? (
                    <CheckCircle className="w-4 h-4 text-white" />
                  ) : (
                    <XCircle className="w-4 h-4 text-white" />
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Drill Question */}
      {selectedDrill && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Target className="w-6 h-6 text-white" />
                <h3 className="text-white font-semibold">
                  練習題 {(drillData.drillQuestions || []).findIndex(d => d.id === selectedDrill.id) + 1}
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-white/20 text-white rounded text-sm">
                  {selectedDrill.topic}
                </span>
                <span className={`px-2 py-1 rounded text-sm ${
                  selectedDrill.difficulty === 'basic' ? 'bg-green-500 text-white' :
                  selectedDrill.difficulty === 'intermediate' ? 'bg-yellow-500 text-white' :
                  'bg-red-500 text-white'
                }`}>
                  {selectedDrill.difficulty === 'basic' ? '基礎' :
                   selectedDrill.difficulty === 'intermediate' ? '中級' : '高級'}
                </span>
                {selectedDrill.metadata?.isRealAIGenerated && (
                  <span className="px-2 py-1 bg-green-500 text-white rounded text-xs">
                    AI
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Question */}
            <div className="mb-6">
              <div className="text-lg mb-4">
                <MathExpression>{selectedDrill.question}</MathExpression>
              </div>
            </div>

            {/* Options - Only show for MC questions */}
            {selectedDrill.isEssayType || (!selectedDrill.options || selectedDrill.options.length === 0) ? (
              // Essay-type question: Show text input
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  請輸入答案：
                </label>
                {userAnswers[selectedDrill.id] === undefined ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={currentInput[selectedDrill.id] || ''}
                      onChange={(e) => handleInputChange(selectedDrill.id, e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, selectedDrill.id)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="輸入你的答案..."
                    />
                    <button
                      onClick={() => handleSubmitAnswer(selectedDrill.id)}
                      disabled={!currentInput[selectedDrill.id] || currentInput[selectedDrill.id].trim() === ''}
                      className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      提交答案
                    </button>
                  </div>
                ) : (
                  <div className="mt-2">
                    <div className={`p-3 rounded-lg ${
                      userAnswers[selectedDrill.id] === (selectedDrill.answer || selectedDrill.correctAnswer)
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center space-x-2">
                        {userAnswers[selectedDrill.id] === (selectedDrill.answer || selectedDrill.correctAnswer) ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-green-800 font-medium">答案正確！</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-red-600" />
                            <span className="text-red-800 font-medium">答案不正確</span>
                          </>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-gray-700">
                        <div>你的答案：{userAnswers[selectedDrill.id]}</div>
                        <div>正確答案：{selectedDrill.answer || selectedDrill.correctAnswer}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // MC question: Show multiple choice options
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {selectedDrill.options && selectedDrill.options.map((option) => {
                  const userAnswer = userAnswers[selectedDrill.id];
                  const isSelected = userAnswer === option.id;
                  const isCorrect = option.id === selectedDrill.correctAnswer;
                  const showCorrect = userAnswer !== undefined;
                  
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleAnswerSelect(selectedDrill.id, option.id)}
                      disabled={userAnswer !== undefined}
                    className={`p-4 text-left rounded-lg border-2 transition-all ${
                      showCorrect
                        ? isCorrect
                          ? 'border-green-500 bg-green-50'
                          : isSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200'
                        : isSelected
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        showCorrect
                          ? isCorrect
                            ? 'bg-green-500 text-white'
                            : isSelected
                              ? 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-700'
                          : isSelected
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-700'
                      }`}>
                        {option.id}
                      </span>
                      <div className="flex-1">
                        <MathExpression>{option.text}</MathExpression>
                      </div>
                    </div>
                  </button>
                );
              })}
              </div>
            )}

            {/* Solution (show after answering) */}
            {userAnswers[selectedDrill.id] !== undefined && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  詳細解答 (正確答案: {selectedDrill.correctAnswer})
                </h4>
                <div className="text-green-700">
                  <MathExpression>{selectedDrill.solution}</MathExpression>
                </div>
                {selectedDrill.metadata?.isRealAIGenerated && (
                  <div className="mt-2 text-xs text-green-600">
                    🤖 此為 DeepSeek AI 即時生成內容
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      {!selectedDrill && (
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-green-800 text-center">
            點擊上方的圓形按鈕選擇練習題，完成所有 {drillData.totalDrills} 題後查看總結果
          </p>
        </div>
      )}
    </div>
  );
};

export default PrimaryDrillQuestions; 